import React, { useEffect, useRef } from "react";

/**
 * <ScrollProgressBar /> — thin brand-gradient bar pinned to the very top
 * of the viewport that fills from 0 → 100% as the user scrolls through
 * the whole page. Sets a CSS var --p on the element (0..1) driven by
 * rAF, so the transform happens in CSS without React re-renders.
 *
 * Works on mobile too. Hides itself for prefers-reduced-motion.
 */
export default function ScrollProgressBar() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      el.style.display = "none";
      return;
    }

    let ticking = false;

    const compute = () => {
      ticking = false;
      const doc = document.documentElement;
      const body = document.body;
      const max = (doc.scrollHeight || body.scrollHeight) - window.innerHeight;
      if (max <= 0) {
        el.style.setProperty("--p", "0");
        return;
      }
      const p = Math.max(0, Math.min(1, window.scrollY / max));
      el.style.setProperty("--p", p.toFixed(4));
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(compute);
      }
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return <div ref={ref} className="scroll-progress-bar" aria-hidden />;
}
